import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DiscoveryStatuses } from '../../document-types/discovery/api';
import { useSlugAvailability } from '../../document-types/discovery/useSlugAvailability';

import { ApiError } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useToastStore } from '../../../stores/toastStore';
import { ImprovementSignalsPanel } from '../../document-types/discovery/components/ImprovementSignalsPanel';
import { LayoutVariantManager } from '../../document-types/variants/components/LayoutVariantManager';
import {
  useImprovementSignals,
  useReeducateDocumentType,
} from '../../document-types/discovery/hooks';
import type {
  ConfidenceThresholdsResponse,
  OcrModelTier,
  SaveDocumentTypeRequest,
} from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FieldList } from '../components/FieldList';
import { OcrSourceSelect } from '../components/OcrSourceSelect';
import { ProviderSelect } from '../components/ProviderSelect';
import { PromptPreviewModal } from '../components/PromptPreviewModal';
import {
  DEFAULT_SEGMENTATION_DRAFT,
  SegmentationPanel,
  segmentationDraftFromResponse,
  segmentationRequestFromDraft,
  type SegmentationDraft,
} from '../components/SegmentationPanel';
import { StatusBadge } from '../components/StatusBadge';
import { ThresholdSliders } from '../components/ThresholdSliders';
import { VersionHistory } from '../components/VersionHistory';
import { useConnections } from '../hooks/useConnections';
import { useDocumentType } from '../hooks/useDocumentType';
import {
  useDocumentTypeMutations,
  useRebuildContentUnderstandingAnalyzer,
} from '../hooks/useDocumentTypeMutations';
import { useDocumentTypeVersions } from '../hooks/useDocumentTypeVersions';
import { useLicenseFeatures } from '../hooks/useLicenseFeatures';
import { usePlatformConnections } from '../hooks/usePlatformConnections';
import {
  emptyField,
  fieldsFromResponse,
  useFieldDefinitionState,
  type FieldDefinitionDraft,
} from '../hooks/useFieldDefinitionState';

const DEFAULT_THRESHOLDS: ConfidenceThresholdsResponse = {
  fieldWarning: 0.75,
  fieldCritical: 0.55,
  documentReviewRatio: 0.5,
  documentRejectGlobalScore: 0.3,
};

/**
 * Projects field drafts to a stable, server-meaningful shape for dirty-checking. Crucially it drops
 * the local-only `draftId` — a random UUID assigned afresh on every hydration — which otherwise
 * differs between the hydrated and current snapshots and marks the editor dirty the instant it loads.
 */
function fieldsForSignature(fields: ReadonlyArray<FieldDefinitionDraft>): unknown {
  return fields.map((f) => ({
    name: f.name,
    type: f.type,
    required: f.required,
    critical: f.critical,
    pattern: f.pattern ?? null,
    validationRule: f.validationRule,
    childFields: f.childFields ?? null,
    hint: f.hint ?? null,
    columns: f.columns ?? null,
    children: f.children ? fieldsForSignature(f.children) : null,
  }));
}

/**
 * Editor de un tipo de documento. Mounted en `/document-types/:slug`.
 * El slug especial `new` activa el modo de alta inline.
 *
 * Comportamiento:
 * - Slug `new`: formulario vacío. Al guardar hace PUT /{slug} (create-or-update)
 *   y redirige a `/:newSlug`.
 * - Slug existente: edita el head. "Guardar" hace PUT y actualiza la versión
 *   automáticamente — no hay draft ni publish; todo es edición directa.
 *
 * "Duplicar" se expone en la lista (`DocumentTypesListPage`): abre un modal con
 * un slug pre-rellenado y redirige al editor del nuevo tipo. Aquí no replicamos
 * el botón porque la lista ya tiene todo el contexto.
 */
export function DocumentTypeEditorPage(): JSX.Element {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const isNew = routeSlug === 'new';
  const targetSlug = isNew ? undefined : routeSlug;

  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);
  const mutations = useDocumentTypeMutations();
  const rebuildAnalyzer = useRebuildContentUnderstandingAnalyzer();
  // Resolved provider type drives the visibility of the "Rebuild analyzer (CU)"
  // admin action: it must only appear when this doctype targets a Content
  // Understanding provider (own or platform-managed).
  const { data: tenantProviders } = useConnections();
  const { data: platformProviders } = usePlatformConnections();

  // Whether this tenant may choose the OCR source (platform-admin-granted). Default false until the
  // license status loads, so the picker is never shown to a tenant that lacks the privilege.
  const { data: licenseStatus } = useLicenseFeatures();
  const canEditOcrSource = licenseStatus?.features.canEditOcrSource ?? false;
  // The "exclude from auto-classification" toggle is only meaningful when the tenant is entitled to the
  // feature; otherwise the control is hidden (autonomy-first — no dead knobs).
  const autoClassificationEnabled = licenseStatus?.features.autoClassificationEnabled ?? false;

  const { data: existing, isLoading, isError, error } = useDocumentType(targetSlug);
  const { data: versions } = useDocumentTypeVersions(targetSlug);

  // ---------- Local form state ----------

  const [name, setName] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const [providerId, setProviderId] = useState<string>('');
  const [thresholds, setThresholds] = useState<ConfidenceThresholdsResponse>(DEFAULT_THRESHOLDS);
  const [segmentation, setSegmentation] = useState<SegmentationDraft>(DEFAULT_SEGMENTATION_DRAFT);
  const [ocrModelTier, setOcrModelTier] = useState<OcrModelTier>('Auto');
  // OCR source: null = Azure Document Intelligence default (uses ocrModelTier); a provider id selects a
  // platform OCR provider (uses ocrCapabilities). Both are sent on every save.
  const [ocrProviderId, setOcrProviderId] = useState<string | null>(null);
  const [ocrCapabilities, setOcrCapabilities] = useState<string[]>([]);
  const [excludeFromAutoClassification, setExcludeFromAutoClassification] = useState<boolean>(false);

  const fieldState = useFieldDefinitionState([]);

  // Live slug-availability feedback in create mode — mirrors the pattern used by DiscoveryPublishStep.
  const slugAvailability = useSlugAvailability(isNew ? slug : '');

  const [promptOpen, setPromptOpen] = useState<boolean>(false);
  const [reeducateOpen, setReeducateOpen] = useState<boolean>(false);
  // Empty string = "use the type's own configured model" (backend smart default).
  const [reeducateProviderId, setReeducateProviderId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'editor' | 'signals'>('editor');
  const reeducateMutation = useReeducateDocumentType();

  // Phase 6 Wave 11.F — surface the "Mejoras sugeridas" tab only when there is
  // at least one active improvement signal, per PHASE_6.md acceptance criteria.
  const signalsQuery = useImprovementSignals(
    !isNew && existing !== undefined ? existing.slug : undefined,
  );
  const hasSignals = (signalsQuery.data ?? []).length > 0;

  // Hydrate from server response.
  useEffect(() => {
    if (existing === undefined) {
      return;
    }
    setName(existing.name);
    setSlug(existing.slug);
    setProviderId(existing.modelProviderId);
    setThresholds(existing.thresholds);
    setSegmentation(segmentationDraftFromResponse(existing.segmentation));
    setOcrModelTier(existing.ocrModelTier ?? 'Auto');
    setOcrProviderId(existing.ocrProviderId ?? null);
    setOcrCapabilities(existing.ocrCapabilities ?? []);
    setExcludeFromAutoClassification(existing.excludeFromAutoClassification ?? false);
    fieldState.setFields(fieldsFromResponse(existing.fields));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-hydrate when the server doc changes
  }, [existing]);

  // Resolve the provider type of the selected `providerId` by looking it up in
  // the tenant providers first and then in the platform-managed list. We only
  // need to know whether the type is ContentUnderstanding to toggle the rebuild
  // button — anything else collapses to `null`.
  const isContentUnderstandingProvider = useMemo<boolean>(() => {
    if (providerId === '') {
      return false;
    }
    const tenantHit = (tenantProviders ?? []).find((p) => p.providerId === providerId);
    if (tenantHit !== undefined) {
      return tenantHit.type.toLowerCase() === 'contentunderstanding';
    }
    const platformHit = (platformProviders ?? []).find((p) => p.providerId === providerId);
    if (platformHit !== undefined) {
      return platformHit.providerType.toLowerCase() === 'contentunderstanding';
    }
    return false;
  }, [providerId, tenantProviders, platformProviders]);

  const handleRebuildAnalyzer = (): void => {
    if (isNew || existing === undefined) {
      return;
    }
    rebuildAnalyzer.mutate(existing.slug, {
      onSuccess: (data) => {
        pushToast({
          tone: 'success',
          title: 'Analyzer reconstruido',
          description: `Analyzer ${data.providerId} listo (${new Date(
            data.rebuildAt,
          ).toLocaleTimeString()}).`,
        });
      },
      onError: (err) => {
        pushToast({
          tone: 'error',
          title: 'No se pudo reconstruir',
          description: err.message,
        });
      },
    });
  };

  // Dirty-state tracking: compare the current form snapshot against what the server returned.
  const hydratedSignature = useMemo<string | null>(() => {
    if (existing === undefined) {
      return null;
    }
    return JSON.stringify({
      name: existing.name,
      slug: existing.slug,
      providerId: existing.modelProviderId,
      thresholds: existing.thresholds,
      segmentation: segmentationDraftFromResponse(existing.segmentation),
      ocrModelTier: existing.ocrModelTier ?? 'Auto',
      ocrProviderId: existing.ocrProviderId ?? null,
      ocrCapabilities: [...(existing.ocrCapabilities ?? [])].sort(),
      excludeFromAutoClassification: existing.excludeFromAutoClassification ?? false,
      fields: fieldsForSignature(fieldsFromResponse(existing.fields)),
    });
  }, [existing]);

  const currentSignature = useMemo<string>(
    () =>
      JSON.stringify({
        name,
        slug,
        providerId,
        thresholds,
        segmentation,
        ocrModelTier,
        ocrProviderId,
        ocrCapabilities: [...ocrCapabilities].sort(),
        excludeFromAutoClassification,
        fields: fieldsForSignature(fieldState.fields),
      }),
    [
      name,
      slug,
      providerId,
      thresholds,
      segmentation,
      ocrModelTier,
      ocrProviderId,
      ocrCapabilities,
      excludeFromAutoClassification,
      fieldState.fields,
    ],
  );

  // For "new" we treat the empty form as not dirty.
  const isDirty = isNew
    ? name.trim() !== '' || slug.trim() !== '' || fieldState.fields.length > 0
    : hydratedSignature !== null && hydratedSignature !== currentSignature;

  // beforeunload covers tab close / refresh / external nav.
  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const confirmIfDirty = (): boolean => {
    if (!isDirty) {
      return true;
    }
    return window.confirm(
      'Tienes cambios sin guardar en este tipo de documento. ¿Descartarlos y salir?',
    );
  };

  // ---------- Handlers ----------

  const handleSave = (): void => {
    if (fieldState.fields.length === 0) {
      pushToast({
        tone: 'warning',
        title: 'Añade al menos un campo',
        description: 'Un tipo de documento sin campos no puede ejecutar extracciones.',
      });
      return;
    }
    if (name.trim() === '' || providerId === '') {
      pushToast({
        tone: 'warning',
        title: 'Faltan datos',
        description: 'Nombre y modelo IA son obligatorios.',
      });
      return;
    }
    const emptyNameField = fieldState.fields.find((f) => f.name.trim() === '');
    if (emptyNameField !== undefined) {
      pushToast({
        tone: 'warning',
        title: 'Nombre de campo vacío',
        description: 'Todos los campos deben tener un nombre antes de guardar.',
      });
      return;
    }

    const trimmedSlug = isNew ? slug.trim() : (existing?.slug ?? slug.trim());

    if (isNew) {
      if (trimmedSlug === '') {
        pushToast({ tone: 'warning', title: 'Indica un slug.' });
        return;
      }
      if (!/^[a-z0-9_-]+$/.test(trimmedSlug)) {
        pushToast({
          tone: 'warning',
          title: 'Slug inválido',
          description: 'Solo se permiten letras minúsculas, números, guiones y guiones bajos.',
        });
        return;
      }
    }

    const segmentationReq = segmentationRequestFromDraft(
      segmentation,
      existing?.segmentation != null,
    );
    const body: SaveDocumentTypeRequest = {
      name: name.trim(),
      modelProviderId: providerId,
      fields: fieldState.toApiPayload(),
      thresholds,
      ocrModelTier,
      // OCR source: null id + empty capabilities for the DI default; the chosen provider id + its
      // selected capability names otherwise. Always sent so the server never leaves capabilities untouched.
      ocrProviderId,
      ocrCapabilities: ocrProviderId !== null ? ocrCapabilities : [],
      excludeFromAutoClassification,
      ...(segmentationReq !== null ? { segmentation: segmentationReq } : {}),
      // Carry the caller's intent so the server enforces it as a safety net.
      ...(isNew ? { expectNew: true } : { expectNew: false }),
    };
    mutations.save.mutate(
      { slug: trimmedSlug, body },
      {
        onSuccess: (saved) => {
          if (isNew) {
            pushToast({ tone: 'success', title: `Tipo "${saved.slug}" guardado.` });
            navigate(`/document-types/${encodeURIComponent(saved.slug)}`, { replace: true });
          } else {
            pushToast({ tone: 'success', title: `Cambios guardados (v${saved.version}).` });
          }
        },
        onError: (err) => surfaceError(err, 'Error al guardar', pushToast, isNew),
      },
    );
  };

  const handleReeducateConfirm = (): void => {
    if (existing === undefined) {
      return;
    }
    if (!confirmIfDirty()) {
      return;
    }
    reeducateMutation.mutate({
      slug: existing.slug,
      modelProviderId: reeducateProviderId !== '' ? reeducateProviderId : null,
    }, {
      onSuccess: (draft) => {
        setReeducateOpen(false);
        if (draft.status === DiscoveryStatuses.Analyzing) {
          pushToast({
            tone: 'success',
            title: 'Reeducación iniciada',
            description: `Analizando ${draft.goldenSamplesUsed ?? 0} muestra(s) dorada(s) de producción.`,
          });
          navigate(`/document-types/discover/${encodeURIComponent(draft.id)}/analyzing`);
        } else if (draft.status === DiscoveryStatuses.Created) {
          pushToast({
            tone: 'info',
            title: 'Sin muestras doradas todavía',
            description: 'Sube entre 1 y 10 PDFs representativos para reeducar este tipo.',
          });
          navigate(`/document-types/discover/${encodeURIComponent(draft.id)}/upload`);
        } else {
          pushToast({
            tone: 'success',
            title: 'Reeducación iniciada.',
            description: `Continúa la revisión sobre "${existing.slug}".`,
          });
          navigate(`/document-types/discover/${encodeURIComponent(draft.id)}/review`);
        }
      },
      onError: (err) => {
        surfaceError(err, 'No se pudo iniciar la reeducación', pushToast);
        setReeducateOpen(false);
      },
    });
  };

  const handleRollback = (version: string): void => {
    if (isNew || existing === undefined) {
      return;
    }
    mutations.rollback.mutate(
      { slug: existing.slug, toVersion: version },
      {
        onSuccess: (restored) => {
          pushToast({
            tone: 'success',
            title: `Restaurado a v${version} (nueva versión: v${restored.version}).`,
          });
        },
        onError: (err) => surfaceError(err, 'Error al restaurar', pushToast),
      },
    );
  };

  // ---------- Render ----------

  if (isLoading && !isNew) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError && !isNew) {
    const message = error instanceof Error ? error.message : 'Error desconocido.';
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <strong className="font-semibold">No se pudo cargar el tipo.</strong>
        <p className="mt-1">{message}</p>
      </div>
    );
  }

  const isSaving = mutations.save.isPending;
  const slugTaken = isNew && slugAvailability.kind === 'taken';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">
              {isNew ? 'Nuevo tipo de documento' : existing?.name ?? slug}
            </h1>
            {existing !== undefined ? <StatusBadge version={existing.version} /> : null}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {isNew
              ? 'Define el nombre, slug y campos. Guarda para crear el tipo.'
              : `Versión actual: ${existing?.version ?? '—'}`}
            {isDirty ? <span className="ml-2 text-amber-700">· cambios sin guardar</span> : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew ? (
            <button
              type="button"
              onClick={() => setPromptOpen(true)}
              className="btn-secondary"
              disabled={existing === undefined}
            >
              Vista previa del prompt
            </button>
          ) : null}
          {!isNew && existing !== undefined ? (
            <button
              type="button"
              onClick={() => setReeducateOpen(true)}
              className="btn-secondary"
              disabled={reeducateMutation.isPending}
              data-testid="reeducate-button"
            >
              Reeducar
            </button>
          ) : null}
          {!isNew && existing !== undefined && isContentUnderstandingProvider ? (
            <button
              type="button"
              onClick={handleRebuildAnalyzer}
              className="btn-secondary"
              disabled={rebuildAnalyzer.isPending}
              data-testid="rebuild-cu-analyzer-button"
              title="Borra y vuelve a crear el analyzer de Content Understanding asociado a este tipo."
            >
              {rebuildAnalyzer.isPending ? 'Reconstruyendo…' : 'Reconstruir analyzer (CU)'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={isSaving || slugTaken}
            data-testid="save-button"
            title={slugTaken ? 'El slug ya está en uso — elige otro.' : undefined}
          >
            {isSaving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </header>

      {hasSignals && existing !== undefined ? (
        <nav aria-label="Pestañas del tipo" className="flex border-b border-slate-200 text-sm">
          <button
            type="button"
            onClick={() => setActiveTab('editor')}
            className={
              'px-4 py-2 font-medium ' +
              (activeTab === 'editor'
                ? 'border-b-2 border-primary text-primary'
                : 'text-slate-500 hover:text-slate-700')
            }
          >
            Editor
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('signals')}
            className={
              'px-4 py-2 font-medium ' +
              (activeTab === 'signals'
                ? 'border-b-2 border-primary text-primary'
                : 'text-slate-500 hover:text-slate-700')
            }
          >
            Mejoras sugeridas
            <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
              {(signalsQuery.data ?? []).length}
            </span>
          </button>
        </nav>
      ) : null}

      {activeTab === 'signals' && hasSignals && existing !== undefined ? (
        <ImprovementSignalsPanel slug={existing.slug} />
      ) : (
        <>
          <section className="card p-5">
            <h2 className="text-base font-semibold text-slate-900">Identidad</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="label" htmlFor="dt-name">
                  Nombre
                </label>
                <input
                  id="dt-name"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="dt-slug">
                  Slug {isNew ? '' : '(inmutable)'}
                </label>
                <input
                  id="dt-slug"
                  className="input font-mono text-sm"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={!isNew}
                  placeholder="factura"
                  aria-describedby={isNew ? 'slug-hint' : undefined}
                />
                {isNew ? (
                  <p
                    id="slug-hint"
                    className={
                      'mt-1 text-xs ' +
                      (slugAvailability.kind === 'taken'
                        ? 'text-rose-600'
                        : slugAvailability.kind === 'free'
                          ? 'text-emerald-600'
                          : 'text-slate-400')
                    }
                    data-testid="slug-hint"
                  >
                    {slugAvailability.kind === 'taken'
                      ? `Slug ya está en uso${slugAvailability.suggestion !== null ? ` — prueba "${slugAvailability.suggestion}"` : ''}.`
                      : slugAvailability.kind === 'free'
                        ? 'Slug disponible.'
                        : slugAvailability.kind === 'checking'
                          ? 'Comprobando disponibilidad…'
                          : 'Solo letras minúsculas, números, guiones y guiones bajos.'}
                  </p>
                ) : null}
              </div>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="text-base font-semibold text-slate-900">Procesado: OCR → IA</h2>
            <p className="mt-1 text-sm text-slate-500">
              Cada documento pasa por dos pasos: primero un motor OCR extrae el texto, y después un modelo
              de IA lo convierte en JSON estructurado. Configura cada paso de forma independiente.
            </p>
            <div className="mt-4 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">1 · Fuente OCR</h3>
                <div className="mt-3">
                  <OcrSourceSelect
                    ocrProviderId={ocrProviderId}
                    ocrModelTier={ocrModelTier}
                    ocrCapabilities={ocrCapabilities}
                    canEdit={canEditOcrSource}
                    onChange={({ providerId: nextProviderId, capabilities }) => {
                      setOcrProviderId(nextProviderId);
                      setOcrCapabilities(capabilities);
                    }}
                    onTierChange={setOcrModelTier}
                    onCapabilitiesChange={setOcrCapabilities}
                  />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-700">2 · Modelo de IA (→ JSON)</h3>
                <div className="mt-3">
                  <label className="label" htmlFor="dt-llm-provider">
                    Proveedor de IA
                  </label>
                  <ProviderSelect value={providerId} onChange={setProviderId} />
                  <p className="mt-1 text-xs text-slate-500">
                    Convierte el texto del OCR en el JSON definido por los campos de abajo.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {autoClassificationEnabled ? (
            <section className="card p-5">
              <h2 className="text-base font-semibold text-slate-900">Clasificación automática</h2>
              <p className="mt-1 text-sm text-slate-500">
                Cuando un documento se sube en modo «Automático», el sistema elige entre los tipos
                publicados del tenant. Excluye este tipo si no debe considerarse nunca como candidato
                (por ejemplo, un tipo genérico o de pruebas).
              </p>
              <label className="mt-3 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={excludeFromAutoClassification}
                  onChange={(e) => setExcludeFromAutoClassification(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  data-testid="exclude-from-auto-classification"
                />
                <span className="text-sm text-slate-700">
                  <span className="font-medium text-slate-900">
                    Excluir de la clasificación automática
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Este tipo no aparecerá como candidato al detectar el tipo de un documento subido en
                    modo automático.
                  </span>
                </span>
              </label>
            </section>
          ) : null}

          <section className="card p-5">
            <h2 className="text-base font-semibold text-slate-900">Campos</h2>
            <p className="mt-1 text-sm text-slate-500">
              Arrastra para reordenar. El orden afecta a cómo se sugiere la extracción en el prompt.
            </p>
            <div className="mt-4">
              <FieldList
                fields={fieldState.fields}
                setFields={fieldState.setFields}
                onUpdate={fieldState.update}
                onRemove={fieldState.remove}
                onAdd={() => fieldState.setFields([...fieldState.fields, emptyField()])}
                onSetChildren={fieldState.setChildren}
                onChildAdd={fieldState.addChild}
                onChildRemove={fieldState.removeChild}
                onChildUpdate={fieldState.updateChild}
              />
            </div>
          </section>

          <section className="card p-5">
            <h2 className="text-base font-semibold text-slate-900">Umbrales de confianza</h2>
            <p className="mt-1 text-sm text-slate-500">
              Estos umbrales determinan cuándo un campo entra en aviso o crítico y cuándo el
              documento completo requiere revisión humana.
            </p>
            <div className="mt-4">
              <ThresholdSliders value={thresholds} onChange={setThresholds} />
            </div>
          </section>

          {!isNew ? (
            <section className="card p-5">
              <h2 className="text-base font-semibold text-slate-900">Segmentación de documentos</h2>
              <p className="mt-1 text-sm text-slate-500">
                Divide un fichero que contiene varios documentos del mismo tipo en documentos
                independientes, cada uno con su propia ficha y revisión.
              </p>
              <div className="mt-4">
                <SegmentationPanel
                  value={segmentation}
                  onChange={setSegmentation}
                  fieldNames={fieldState.fields.map((f) => f.name)}
                  fieldPatterns={Object.fromEntries(
                    fieldState.fields.map((f) => [f.name, f.pattern]),
                  )}
                />
              </div>
            </section>
          ) : null}

          {!isNew && versions !== undefined && versions.length > 0 ? (
            <section className="card p-5">
              <h2 className="text-base font-semibold text-slate-900">Historial de versiones</h2>
              <p className="mt-1 text-sm text-slate-500">
                Selecciona una versión para ver los cambios respecto a la anterior. Puedes restaurar
                una versión anterior como una nueva versión hacia adelante.
              </p>
              <div className="mt-4">
                {existing !== undefined ? (
                  <VersionHistory
                    versions={versions}
                    onRollback={handleRollback}
                    rollbackDisabled={mutations.rollback.isPending}
                  />
                ) : (
                  <VersionHistory versions={versions} />
                )}
              </div>
            </section>
          ) : null}

          {!isNew && existing !== undefined ? (
            <LayoutVariantManager
              slug={existing.slug}
              fieldNames={existing.fields.map((f) => f.name)}
            />
          ) : null}

          {!isNew && existing !== undefined ? (
            <>
              <PromptPreviewModal
                slug={existing.slug}
                open={promptOpen}
                onClose={() => setPromptOpen(false)}
              />
              <ConfirmDialog
                open={reeducateOpen}
                title="Reeducar tipo de documento"
                description={
                  <span className="block space-y-3">
                    <span className="block">
                      El sistema usará los documentos reales de producción ya revisados (muestras doradas) y
                      sus señales de corrección para proponer mejoras en el esquema de{' '}
                      <code className="font-mono">{existing.slug}</code>. Si aún no hay muestras doradas, se
                      pedirá subir PDFs representativos. El tipo seguirá activo y editable durante el proceso.
                      ¿Continuar?
                    </span>
                    <span className="block">
                      <span className="label block">Modelo IA para la reeducación</span>
                      <ProviderSelect
                        value={reeducateProviderId}
                        onChange={setReeducateProviderId}
                        disabled={reeducateMutation.isPending}
                      />
                      <span className="mt-1 block text-xs text-slate-500">
                        Opcional. Si no eliges, se usa el modelo configurado de este tipo.
                      </span>
                    </span>
                  </span>
                }
                confirmLabel={reeducateMutation.isPending ? 'Iniciando…' : 'Confirmar'}
                busy={reeducateMutation.isPending}
                onConfirm={handleReeducateConfirm}
                onCancel={() => setReeducateOpen(false)}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function surfaceError(
  err: unknown,
  title: string,
  pushToast: ReturnType<typeof useToastStore.getState>['push'],
  isCreate = false,
): void {
  if (err instanceof ApiError) {
    // In create mode a 422 most likely means the slug is already taken — make it actionable.
    const description =
      isCreate && err.status === 422
        ? `Slug ya existe — ${err.message}`
        : err.message;
    pushToast({ tone: 'error', title, description });
    return;
  }
  if (err instanceof Error) {
    pushToast({ tone: 'error', title, description: err.message });
    return;
  }
  pushToast({ tone: 'error', title, description: 'Error desconocido.' });
}
